import HeroBusiness from "@/components/common/HeroBusiness";
import PageTitleBox from "@/components/common/PageTitleBox";
import Container from "@/components/layout/Container";
import { useState } from "react";

type TabType = "agent" | "tcp" | "api";

const flowSteps = {
  agent: [
    { label: "고객사가", sublabel: "문자를 전송" },
    { label: "자사 Agent가", sublabel: "내용을 감지" },
    { label: "자동으로", sublabel: "자사 서버로 전송" },
  ],
  tcp: [
    { label: "고객사 시스템이", sublabel: "문자 전송" },
    { label: "자사와 고객사", sublabel: "TCP 직접 통신" },
    { label: "자사 게이트웨이가", sublabel: "수신하여 처리" },
  ],
  api: [
    { label: "고객사의", sublabel: "메시지 발송 요청" },
    { label: "HTTP Request를", sublabel: "통해 데이터 전송" },
    { label: "자사에서 데이터를", sublabel: "수신하여 처리" },
  ],
};

const tabContents = {
  agent: {
    title: "Agent 방식",
    intro:
      "고객사가 문자를 전송하면 본인의 Database에 저장이 되고, Agent(중간 관리자)를 거쳐 클라이언트 자사 서버로 메시지 내용이 전송을 하는 방식 입니다.",
    table: [
      {
        th: "작동 흐름",
        td: "[고객사 DB] ⇄ [중앙 메시지 서버 (Agent)] ⇄ [스탠다드 메시징 G/W] ⇄ [이동통신사]",
      },
      {
        th: "방식 특징",
        td: "· DB 연동만으로 즉시 구축 가능\n· 자동 감지 및 처리\n· 간편한 설정",
      },
      {
        th: "권장 대상",
        td: "빠른 구축과 손쉬운 관리를 원하는 일반 기관",
      },
    ],
  },
  tcp: {
    title: "TCP 직연동 방식",
    intro:
      "Agent(중간 관리자) 없이 고객사 서버와 자사 게이트웨이가 통신 프로토콜을 이용하여 직접 연결해 문자 데이터를 실시간 전달하는 방식입니다.",
    table: [
      {
        th: "작동 흐름",
        td: "[타사 Agent/서버] ⇄ [스탠다드 메시징 G/W] ⇄ [이동통신사]",
      },
      {
        th: "방식 특징",
        td: "· 대량 트래픽 처리 최적화\n· 높은 실시간성 보장\n· 안정적인 고속 처리",
      },
      {
        th: "권장 대상",
        td: "금융권, 대형 사업자 등 고속 안정성 필요 기관",
      },
    ],
  },
  api: {
    title: "API 방식",
    intro:
      "복잡한 TCP 연동 대신 표준화된 REST API를 제공하여, 별도 설치 없이 HTTP 요청만으로 간편하게 문자를 발송합니다.",
    table: [
      {
        th: "작동 흐름",
        td: "[고객 앱/웹] ⇄ (HTTP Request) ⇄ [스탠다드 API 서버] ⇄ [이동통신사]",
      },
      {
        th: "방식 특징",
        td: "· DB 연동만으로 즉시 구축 가능\n· 높은 호환성 제공\n· 웹/앱 서비스 등 유연한 개발 환경에 신속 적용 가능",
      },
      {
        th: "권장 대상",
        td: "빠른 구축과 손쉬운 관리를 원하는 일반 기관",
      },
    ],
  },
};

const BusinessIntegration = () => {
  const [activeTab, setActiveTab] = useState<TabType>("agent");

  const currentFlow = flowSteps[activeTab];
  const currentContent = tabContents[activeTab];

  return (
    <Container>
      <HeroBusiness title="비즈니스를 완성하는" />

      <section id="main-content">
        <div className="mx-auto px-8 lg:px-[8rem] py-[70px]">
          <PageTitleBox
            subtitle={
              <>
                고객 환경에 최적화된 3가지 연동 방식(Agent, TCP, REST API)을 지원하여,
                <br />
                어떤 인프라에서도 가장 빠르고 경제적인 메시징을 구현합니다.
              </>
            }
            showSeparator={false}
          />

          {/* Flow Diagram */}
          <div className="mb-32 w-full max-w-[900px] mx-auto">
            <div className="flex items-end justify-center">
              {currentFlow.flatMap((step, idx) => {
                const elements = [
                  <div
                    key={`step-${idx}`}
                    className="flex flex-col items-center w-[140px] flex-shrink-0"
                  >
                    <div className="text-[1.4rem] text-gray-600 text-center leading-tight mb-1">
                      {step.label}
                    </div>
                    <div className="text-[1.4rem] text-gray-600 text-center leading-tight mb-6">
                      {step.sublabel}
                    </div>
                    <div className="w-14 h-14 rounded-full bg-[#1a2b5f] text-white flex items-center justify-center text-[1.8rem] font-bold">
                      {idx + 1}
                    </div>
                  </div>,
                ];

                if (idx < currentFlow.length - 1) {
                  elements.push(
                    <div
                      key={`line-${idx}`}
                      className="flex-1 h-[2px] bg-[#1a2b5f] mb-[28px]"
                    ></div>
                  );
                }

                return elements;
              })}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex border-b-2 border-gray-200" role="tablist">
              {(["agent", "tcp", "api"] as TabType[]).map(tab => (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-8 py-3 text-[1.5rem] font-medium transition-colors relative ${
                    activeTab === tab ? "text-[#1a2b5f]" : "text-gray-400"
                  }`}
                >
                  {tab === "agent" ? "Agent 방식" : tab === "tcp" ? "TCP 직연동 방식" : "API 방식"}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#1a2b5f]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div>
            <h3 className="text-[2.2rem] font-bold mb-3 text-gray-900">{currentContent.title}</h3>
            <p className="text-[1.5rem] text-gray-600 leading-[1.7] mb-10">
              {currentContent.intro}
            </p>

            <table className="w-full border border-gray-200">
              <tbody>
                {currentContent.table.map((row, idx) => (
                  <tr
                    key={idx}
                    className={
                      idx < currentContent.table.length - 1 ? "border-b border-gray-200" : ""
                    }
                  >
                    <th className="text-left py-6 px-8 bg-gray-50 text-[1.5rem] font-semibold text-gray-900 w-[180px] align-top border-r border-gray-200">
                      {row.th}
                    </th>
                    <td className="py-6 px-8 text-[1.5rem] text-gray-700 whitespace-pre-line leading-[1.8]">
                      {row.td}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </Container>
  );
};

export default BusinessIntegration;
